// External Libraries
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';

// UI Components
import FormField from '../../components/ui/FormField';
import CheckboxField from '../../components/ui/CheckboxField';
import MediaSelectorButton from '../../components/media/MediaSelectorButton';
import SubmitButton from '../../components/ui/SubmitButton';

// Redux Actions
import { getPartnersFunc } from '../../../store/custom-actions/partners-actions/GetPartnersSlicer';
import { updatePartnerFunc } from '../../../store/custom-actions/partners-actions/UpdatePartnerSlicer';

function UpdatePartnerForm({ show, onHide, partnerId, oldName, oldLogoId, oldSortOrder, oldStatus, oldLogo }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  const { register, reset, formState: { errors }, handleSubmit, setValue } = useForm();
  const { loading: submitLoading } = useSelector(state => state.updatePartnerSlicer);

  const [logoDefault, setLogoDefault] = useState([]);

  // Populate form data when modal opens
  useEffect(() => {
    if (show && partnerId) {
      setValue("name", oldName || "");
      setValue("sort_order", oldSortOrder || 0);
      setValue("is_active", oldStatus || false);
      setValue("logo_id", oldLogoId || null);
      
      // Set default logo for media selector
      if (oldLogo) {
        setLogoDefault([oldLogo]);
      } else {
        setLogoDefault([]);
      }
    }
  }, [show, partnerId, oldName, oldLogoId, oldSortOrder, oldStatus, oldLogo, setValue]);

  const resetForm = () => {
    onHide();
    reset();
    setLogoDefault([]);
  };

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      is_active: data.is_active || false,
      sort_order: parseInt(data.sort_order) || 0,
    };
    
    const result = await dispatch(updatePartnerFunc({ id: partnerId, data: payload }));
    
    if (updatePartnerFunc.fulfilled.match(result)) {
      dispatch(getPartnersFunc());
      resetForm();
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg">
        <Modal.Header>
          <Modal.Title>{t('Update Partner')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form
            id="submit-form-update"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row">
              <div className="col-md-6">
                <FormField
                  label={t("Name")}
                  name="name"
                  register={register}
                  required={true}
                  errors={errors}
                  placeholder={t("Enter partner name")}
                />
              </div>
              <div className="col-md-6">
                <FormField
                  label={t("Sort Order")}
                  name="sort_order"
                  type="number"
                  register={register}
                  errors={errors}
                  placeholder={t("Enter sort order")}
                  min="0"
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <MediaSelectorButton
                  label={t("Logo")}
                  inputName="logo_id"
                  register={register}
                  errors={errors}
                  onChange={(val) => setValue("logo_id", val)}
                  defaultSelected={logoDefault}
                />
              </div>
              <div className="col-md-6 d-flex align-items-end">
                <CheckboxField
                  name="is_active"
                  label={t("Active")}
                  register={register}
                  errors={errors}
                />
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            {t('Close')}
          </Button>
          <SubmitButton
            isLoading={submitLoading}
            form="submit-form-update"
          />
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default UpdatePartnerForm;
